import { Button, Toggle, Typography } from "@cur8/maneki";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { isNekoLensSite } from "lib/nekoLens";
import { useCallback } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import styles from "./styles.module.sass";

interface FeatureFlagsViewProps {
  onBack(): void;
}

export function FeatureFlagsView({ onBack }: FeatureFlagsViewProps) {
  const featurePreferences = useFeaturePreference();
  // Neko Lens only runs in some markets. The toggle still shows elsewhere so
  // operators can see the setting exists, it just can't be turned on.
  const dermaQrCodeSupported = isNekoLensSite(
    getAndParseLocationCookie()?.siteId
  );

  const onToggleRoomScanWarning = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        roomScanWarningEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  const onToggleDermaQrCode = (isSelected: boolean) => {
    if (!dermaQrCodeSupported) {
      return;
    }
    featurePreferences.set({
      ...featurePreferences.data,
      dermaQrCodeEnabled: isSelected,
    });
  };

  const onToggleCheckoutFlow = useCallback(
    (isSelected: boolean) => {
      featurePreferences.set({
        ...featurePreferences.data,
        checkoutFlowEnabled: isSelected,
      });
    },
    [featurePreferences]
  );

  return (
    <PageFrameContent>
      <div className={styles.FeatureFlagsView}>
        <Typography variant="display-s">Settings</Typography>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Room scan warning"
            isSelected={featurePreferences.data.roomScanWarningEnabled}
            onChange={onToggleRoomScanWarning}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Room scan warning</Typography>
            <Typography variant="body-s">
              Warn before assigning a room to a member when another member is
              actively being scanned in it.
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Derma QR code"
            isDisabled={!dermaQrCodeSupported}
            isSelected={
              dermaQrCodeSupported && featurePreferences.data.dermaQrCodeEnabled
            }
            onChange={onToggleDermaQrCode}
          />
          <div className={styles.text}>
            <Typography
              variant="label-m"
              color={dermaQrCodeSupported ? "default" : "subtle"}
            >
              Derma QR code
            </Typography>
            <Typography variant="body-s" color="subtle">
              {dermaQrCodeSupported
                ? "Show the Neko Lens QR code for Derma exams in this clinic."
                : "Neko Lens is not available in this clinic's market."}
            </Typography>
          </div>
        </div>

        <div className={styles.featureToggle}>
          <Toggle
            aria-label="Check-out flow"
            isSelected={featurePreferences.data.checkoutFlowEnabled}
            onChange={onToggleCheckoutFlow}
          />
          <div className={styles.text}>
            <Typography variant="label-m">Check-out flow</Typography>
            <Typography variant="body-s">
              Turn on the reworked check-out steps in this clinic. Check-out
              still works with this off since it keeps the current screens.
            </Typography>
          </div>
        </div>
      </div>

      <ActionsFooter
        left={
          <Button variant="secondary" onPress={onBack} fillContainer>
            Back
          </Button>
        }
      />
    </PageFrameContent>
  );
}
